import { useEffect, useRef } from 'react';
import { filesFromClipboard } from '@components/framework/uploadpanel/fileTransfer.ts';

type PasteHandler = (files: File[]) => void;

interface PasteTarget {
  handle: PasteHandler;
}

/**
 * Paste targets are kept in a stack and only the most recently mounted one receives the files. Panels
 * live inside sidebars and dialogs that open on top of each other, and a paste has one obvious
 * destination — the thing the user just opened — so fanning the event out to every mounted panel
 * would upload the same file several times.
 */
const targets: PasteTarget[] = [];
let listening = false;

const isEditableTarget = (target: EventTarget | null): boolean => {
  const element = target as HTMLElement | null;
  if (!element || typeof element.closest !== 'function') return false;
  return element.closest('input:not([type="file"]), textarea, [contenteditable="true"]') !== null;
};

const handlePaste = (event: ClipboardEvent) => {
  const target = targets[targets.length - 1];
  if (!target) return;
  // A paste into a text field belongs to that field, never to the upload panel.
  if (isEditableTarget(event.target)) return;

  const files = filesFromClipboard(event.clipboardData);
  if (files.length === 0) return;

  event.preventDefault();
  target.handle(files);
};

/**
 * Uploads files pasted from the clipboard (Ctrl+V) while the component is mounted.
 *
 * This is the escape hatch for new Outlook: dragging an attachment out of it gives the browser
 * nothing, but "Copy" on the attachment puts a genuine file on the clipboard, which does reach us.
 */
const useFilePaste = (onFiles: PasteHandler, enabled = true) => {
  const handlerRef = useRef(onFiles);

  useEffect(() => {
    handlerRef.current = onFiles;
  });

  useEffect(() => {
    if (!enabled) return;

    const target: PasteTarget = { handle: files => handlerRef.current(files) };
    targets.push(target);
    if (!listening) {
      document.addEventListener('paste', handlePaste);
      listening = true;
    }

    return () => {
      const index = targets.indexOf(target);
      if (index >= 0) targets.splice(index, 1);
      if (targets.length === 0 && listening) {
        document.removeEventListener('paste', handlePaste);
        listening = false;
      }
    };
  }, [enabled]);
};

export default useFilePaste;